import React, { useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Box,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useTranslation } from 'react-i18next';
import { useAuthContext } from '../../contexts/authContext';
import { MDTypography } from '../../components/MDTypography';
import { ContextPanel } from '../../components/ContextPanel';
import { Bill, BillAction } from './ClaimDetailsTypes';
import { BillDetailsView } from './BillDetailsView';
import cms1500Icon from '../../assets/images/1500cms_icon.jpeg';
import ub04Icon from '../../assets/images/ub04_icon.png';
import { BillActionCell } from './components';
import { BillVerdictStatus } from '../../types';

const useStyles = makeStyles((theme) => ({
  tableContainer: {
    margin: theme.spacing(2, 0),
    boxShadow: 'none',
    border: `1px solid ${theme.palette.divider}`,
  },
  table: {
    minWidth: 650,
    '& .MuiTableCell-head': {
      backgroundColor: theme.palette.grey[50],
      fontWeight: 600,
      fontSize: '0.875rem',
    },
    '& .MuiTableCell-body': {
      fontSize: '0.8rem',
      padding: theme.spacing(1),
    },
  },
  actionButton: {
    minWidth: 80,
    padding: theme.spacing(0.5, 1),
    fontSize: '0.75rem',
    margin: theme.spacing(0, 0.5),
  },
  approveButton: {
    backgroundColor: theme.palette.success.main,
    color: theme.palette.success.contrastText,
    '&:hover': {
      backgroundColor: theme.palette.success.dark,
    },
  },
  rejectButton: {
    backgroundColor: theme.palette.error.main,
    color: theme.palette.error.contrastText,
    '&:hover': {
      backgroundColor: theme.palette.error.dark,
    },
  },
  billAmount: {
    fontWeight: 500,
  },
  statusPending: {
    color: theme.palette.warning.main,
    fontWeight: 500,
  },
  statusApproved: {
    color: theme.palette.success.main,
    fontWeight: 500,
  },
  statusRejected: {
    color: theme.palette.error.main,
    fontWeight: 500,
  },
}));

interface ClaimBillsTableProps {
  bills: Bill[];
  claimId: string;
  onBillAction: (billId: number, action: BillAction) => void;
  onBillVerdict: (
    billId: number,
    verdict: BillVerdictStatus,
    amount: number,
    reason: string
  ) => void;
  loading?: boolean;
}

export const ClaimBillsTable: React.FC<ClaimBillsTableProps> = ({
  bills,
  claimId,
  onBillAction,
  onBillVerdict,
  loading = false,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const { user } = useAuthContext();

  const [contextPanelOpen, setContextPanelOpen] = useState(false);
  const [billForDetails, setBillForDetails] = useState<Bill | null>(null);

  // Check if user can perform approve/reject actions
  const canApproveReject = user?.role !== 'data_reader';

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const getStatusClassName = (status: string) => {
    switch (status.toLowerCase()) {
      case 'approved':
      case 'partial-approved':
        return classes.statusApproved;
      case 'rejected':
        return classes.statusRejected;
      default:
        return classes.statusPending;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'approved':
      case 'partial-approved':
        return 'success';
      case 'rejected':
        return 'error';
      default:
        return 'warning';
    }
  };

  if (bills.length === 0) {
    return (
      <Box p={2}>
        <MDTypography variant="body2" color="text">
          {t('No bills found for this claim')}
        </MDTypography>
      </Box>
    );
  }

  return (
    <TableContainer component={Paper} className={classes.tableContainer}>
      <Table className={classes.table} size="small">
        <TableHead>
          <TableRow>
            <TableCell>{t('Type')}</TableCell>
            <TableCell>{t('Bill ID')}</TableCell>
            <TableCell>{t('Provider Name')}</TableCell>
            <TableCell align="right">{t('Charged')}</TableCell>
            <TableCell align="right">{t('Saving')}</TableCell>
            <TableCell align="right">{t('Allowed')}</TableCell>
            <TableCell align="right">{t('Fee')}</TableCell>
            <TableCell align="right">{t('Total')}</TableCell>
            <TableCell align="center">{t('Details')}</TableCell>
            {canApproveReject ? (
              <TableCell align="center">{t('Actions')}</TableCell>
            ) : null}
          </TableRow>
        </TableHead>
        <TableBody>
          {bills.map((bill) => {
            return (
              <TableRow key={bill.id} hover>
                <TableCell
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-around',
                    padding: '10px'
                  }}
                >
                  {bill.billType === 'Professional' && (
                    <img
                      src={cms1500Icon}
                      alt="Professional"
                      style={{ width: 24, height: 'auto' }}
                    />
                  )}
                  {bill.billType === 'Institutional' && (
                    <img
                      src={ub04Icon}
                      alt="Institutional"
                      style={{ width: 24, height: 'auto' }}
                    />
                  )}
                  {/* {bill.billType === 'Professional' ? '1500' : 'UB-04'} */}
                  {bill.billType}
                </TableCell>
                <TableCell>
                  <MDTypography variant="caption" color="secondary">
                    {bill.gemBillNo}
                  </MDTypography>
                </TableCell>
                <TableCell>
                  <MDTypography variant="caption">
                    {bill.payableTo}
                  </MDTypography>
                </TableCell>
                <TableCell align="right" className={classes.billAmount}>
                  {formatCurrency(bill.billedCharges)}
                </TableCell>
                <TableCell align="center" className={classes.billAmount}>
                  {bill.savingsAmount ? formatCurrency(bill.savingsAmount) : 'N/A'}
                </TableCell>
                <TableCell align="center" className={classes.billAmount}>
                    {bill.payable || bill.payable === 0 ? formatCurrency(bill.payable) : 'N/A'}
                </TableCell>
                <TableCell align="center" className={classes.billAmount}>
                  {bill.costContainmentFees ? formatCurrency(bill.costContainmentFees) : 'N/A'}
                </TableCell>
                <TableCell align="center" className={classes.billAmount}>
                  {bill.totalDue ? formatCurrency(bill.totalDue) : 'N/A'}
                </TableCell>
                <TableCell align="center">
                  <Button
                    className={classes.actionButton}
                    onClick={() => {
                      setBillForDetails(bill);
                      setContextPanelOpen(true);
                    }}
                    disabled={loading}
                    size="small"
                    variant="outlined"
                    color="primary"
                  >
                    {t('Details')}
                  </Button>
                </TableCell>
                {canApproveReject && (
                  <TableCell align="center" style={{ padding: '8px 0' }}>
                    <BillActionCell
                      bill={bill}
                      claimId={claimId}
                      loading={loading}
                      displayMode="text"
                      onVerdictUpdate={onBillVerdict}
                    />
                  </TableCell>
                )}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>


      <ContextPanel
        open={contextPanelOpen}
        onClose={() => {
          setContextPanelOpen(false);
          setBillForDetails(null);
        }}
        side="right"
        size="large"
        title={
          billForDetails
            ? `${t('Bill Details')} - ${billForDetails.gemBillNo}`
            : t('Bill Details')
        }
      >
        {billForDetails && <BillDetailsView bill={billForDetails} />}
      </ContextPanel>
    </TableContainer>
  );
};

export default ClaimBillsTable;
